/**
 * The AI wallet (TMMAPP-0113 / TMMAPI-0151, MM-D-131): AI is prepaid, per person, in the
 * subscription's currency. `GET /v1/me/ai-wallet` is the meter (also served on `GET /v1/me/billing`
 * as `aiWallet`); entries page 50 at a time, newest first. Every minor amount is rounded down by
 * tmm-api and rendered as served.
 *
 * Purchase and top-up charge the card on file (card data never touches the app): the SPA sends an
 * amount and a fresh `idempotencyKey` per attempt, nothing else. A `PENDING` charge may carry a
 * one-time `clientAction`, completed through `completeClientAction` and never stored.
 */
import type {
  AiWalletChargeView as WireCharge,
  AiWalletPatchRequest,
  AiWalletEntryPage as WireEntryPage,
  AiWalletEntryView as WireEntry,
  AiWalletView as WireWallet,
  LastRecharge as WireLastRecharge,
  PurchaseRequest,
  TopUpRequest,
} from './generated';
import { request } from './client';
import { normalizeClientAction, type ClientAction } from './billing';

export type AiWalletChargeStatus = NonNullable<WireCharge['status']>;
export type AiWalletChargeKind = NonNullable<WireCharge['kind']>;
export type AiWalletCapSource = NonNullable<WireWallet['personCapSource']>;

const CHARGE_STATUSES: readonly AiWalletChargeStatus[] = ['PENDING', 'SUCCEEDED', 'FAILED'];
const CHARGE_KINDS: readonly AiWalletChargeKind[] = ['FIRST_PURCHASE', 'RECHARGE', 'TOP_UP'];
const CAP_SOURCES: readonly AiWalletCapSource[] = ['PERSON', 'PLATFORM'];

/**
 * Every generated wallet-entry kind. A new wire kind fails here until it has a label.
 * `OTHER` is the domain sentinel for a kind this pin does not name — not a wire value.
 */
const KNOWN_ENTRY_KINDS = {
  FIRST_PURCHASE: true,
  RECHARGE: true,
  TOP_UP: true,
  MONTHLY_CREDIT: true,
  ADMIN_CREDIT: true,
  USAGE: true,
  FORFEIT: true,
  REFUND_REVERSAL: true,
  DISPUTE_HOLD: true,
  DISPUTE_RELEASE: true,
  DISPUTE_REVERSAL: true,
  DISPUTE_REINSTATED: true,
} as const satisfies Record<NonNullable<WireEntry['kind']>, true>;

export type AiWalletEntryKind = keyof typeof KNOWN_ENTRY_KINDS | 'OTHER';
export const AI_WALLET_ENTRY_KINDS: readonly (keyof typeof KNOWN_ENTRY_KINDS)[] = Object.keys(
  KNOWN_ENTRY_KINDS,
) as (keyof typeof KNOWN_ENTRY_KINDS)[];

export interface AiWalletLastRecharge {
  status: AiWalletChargeStatus;
  amountMinor: number;
  at: string;
  failureCode: string | null;
}

export interface AiWallet {
  currency: string;
  balanceMinor: number;
  reservedMinor: number;
  availableMinor: number;
  autoRecharge: boolean;
  triggerMinor: number;
  rechargeAmountMinor: number;
  firstPurchaseMinor: number;
  firstPurchaseDone: boolean;
  monthlyCreditMinor: number;
  usedThisPeriodMinor: number;
  periodStart: string | null;
  /**
   * The monthly limit that applies (MM-D-134): the lower of the card's maximum and the person's
   * own; null = none served.
   */
  personCapMinor: number | null;
  /** What the limit leaves this period; null = no limit. */
  personCapRemainingMinor: number | null;
  /** Whose limit `personCapMinor` is: the person's own or the plan's maximum; null for a source this build doesn't know. */
  personCapSource: AiWalletCapSource | null;
  /** The card's maximum AI spend per person per month (TMMAPI-0153). */
  maxAiSpendMinor: number;
  lastRecharge: AiWalletLastRecharge | null;
  /** What a refund or chargeback left owing (TMMAPI-0152, MM-D-133); 0 when none. */
  debtMinor: number;
  /** The automatic recharges allowed in 30 days were used: top up by hand. */
  autoRechargeLimitReached: boolean;
  /** How many automatic recharges are allowed in 30 days (TMMAPI-0162 U6). */
  autoRechargeLimit: number;
}

export interface AiWalletEntry {
  id: number;
  kind: AiWalletEntryKind;
  /** Signed: a credit positive, a debit negative. */
  amountMinor: number;
  at: string;
  /** The AI feature, on a `USAGE` entry. */
  feature: string | null;
  /** A `USAGE` entry whose request failed and was still charged (TMMAPI-0162 U6). */
  failed: boolean;
  /** Tax on a `FIRST_PURCHASE`, `TOP_UP` or `RECHARGE`; null when that charge carried none. */
  taxMinor: number | null;
  /** What the card was charged for that funding entry; null together with `taxMinor`. */
  totalMinor: number | null;
}

export interface AiWalletEntryPage {
  entries: AiWalletEntry[];
  nextCursor: number | null;
}

export interface AiWalletCharge {
  status: AiWalletChargeStatus;
  kind: AiWalletChargeKind | null;
  /** What the wallet is credited (before tax). */
  amountMinor: number;
  /**
   * The tax pay-api (Stripe Tax) calculated on the charge once it succeeded (TMMAPI-0156 U2,
   * MM-D-139). tmm-app never estimates it.
   */
  taxMinor: number;
  /** What the card was charged: amount + tax. */
  totalMinor: number;
  currency: string;
  chargeId: number;
  failureCode: string | null;
  /** One-time, on a `PENDING` answer only; never stored. */
  clientAction: ClientAction | null;
  wallet: AiWallet;
}

function oneOf<T extends string>(values: readonly T[], v: string): T | null {
  return values.find((value) => value === v) ?? null;
}

/** The last recharge; null for a status this build doesn't know. */
function normalizeLastRecharge(w: WireLastRecharge): AiWalletLastRecharge | null {
  const status = oneOf(CHARGE_STATUSES, w.status);
  if (status === null) return null;
  return {
    status,
    amountMinor: w.amountMinor,
    at: w.at,
    failureCode: w.failureCode ?? null,
  };
}

/** The meter. */
export function normalizeAiWallet(w: WireWallet): AiWallet {
  return {
    currency: w.currency,
    balanceMinor: w.balanceMinor,
    reservedMinor: w.reservedMinor,
    availableMinor: w.availableMinor,
    autoRecharge: w.autoRecharge,
    triggerMinor: w.triggerMinor,
    rechargeAmountMinor: w.rechargeAmountMinor,
    firstPurchaseMinor: w.firstPurchaseMinor,
    firstPurchaseDone: w.firstPurchaseDone,
    monthlyCreditMinor: w.monthlyCreditMinor,
    usedThisPeriodMinor: w.usedThisPeriodMinor,
    periodStart: w.periodStart ?? null,
    personCapMinor: w.personCapMinor ?? null,
    personCapRemainingMinor: w.personCapRemainingMinor ?? null,
    personCapSource: oneOf(CAP_SOURCES, w.personCapSource),
    maxAiSpendMinor: w.maxAiSpendMinor,
    lastRecharge: w.lastRecharge == null ? null : normalizeLastRecharge(w.lastRecharge),
    debtMinor: w.debtMinor,
    autoRechargeLimitReached: w.autoRechargeLimitReached,
    autoRechargeLimit: w.autoRechargeLimit,
  };
}

function normalizeEntry(w: WireEntry): AiWalletEntry {
  // An unknown kind is kept as OTHER with its signed amount.
  const kind: AiWalletEntryKind = Object.hasOwn(KNOWN_ENTRY_KINDS, w.kind) ? w.kind : 'OTHER';
  return {
    id: w.id,
    kind,
    amountMinor: w.amountMinor,
    at: w.at,
    feature: w.feature ?? null,
    failed: w.failed ?? false,
    taxMinor: w.taxMinor ?? null,
    totalMinor: w.totalMinor ?? null,
  };
}

export function normalizeAiWalletEntryPage(w: WireEntryPage): AiWalletEntryPage {
  return {
    entries: w.entries.map(normalizeEntry),
    nextCursor: w.nextCursor ?? null,
  };
}

export function normalizeAiWalletCharge(w: WireCharge): AiWalletCharge {
  return {
    // A status this build doesn't know is treated as still settling: the SPA refetches.
    status: oneOf(CHARGE_STATUSES, w.status) ?? 'PENDING',
    kind: oneOf(CHARGE_KINDS, w.kind),
    amountMinor: w.amountMinor,
    taxMinor: w.taxMinor,
    totalMinor: w.totalMinor,
    currency: w.currency,
    chargeId: w.chargeId,
    failureCode: w.failureCode ?? null,
    clientAction: w.clientAction == null ? null : normalizeClientAction(w.clientAction),
    wallet: normalizeAiWallet(w.wallet),
  };
}

export const aiWalletApi = {
  async get(): Promise<AiWallet> {
    return normalizeAiWallet(await request<WireWallet>('GET', '/v1/me/ai-wallet'));
  },
  async setAutoRecharge(autoRecharge: boolean): Promise<AiWallet> {
    const json: AiWalletPatchRequest = { autoRecharge };
    return normalizeAiWallet(await request<WireWallet>('PATCH', '/v1/me/ai-wallet', { json }));
  },
  async entries(cursor?: number | null): Promise<AiWalletEntryPage> {
    return normalizeAiWalletEntryPage(
      await request<WireEntryPage>(
        'GET',
        '/v1/me/ai-wallet/entries',
        cursor == null ? {} : { query: { cursor } },
      ),
    );
  },
  /** The first purchase; `amountMinor` only when the person changed it (default = the terms'). */
  async purchase(idempotencyKey: string, amountMinor?: number): Promise<AiWalletCharge> {
    const json: PurchaseRequest = { idempotencyKey };
    if (amountMinor !== undefined) json.amountMinor = amountMinor;
    return normalizeAiWalletCharge(
      await request<WireCharge>('POST', '/v1/me/ai-wallet/purchase', { json }),
    );
  },
  async topUp(amountMinor: number, idempotencyKey: string): Promise<AiWalletCharge> {
    const json: TopUpRequest = { amountMinor, idempotencyKey };
    return normalizeAiWalletCharge(
      await request<WireCharge>('POST', '/v1/me/ai-wallet/top-up', { json }),
    );
  },
};
